import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Crown, Sparkles, Lock } from '@/components/icons';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  feature: 'diagrams' | 'export-svg' | 'general';
  currentCount?: number;
  maxCount?: number;
}

const featureMessages = {
  diagrams: {
    title: 'Diagram Limit Reached',
    description: "You've reached your diagram limit on the Free plan.",
    icon: Lock,
    benefits: [
      'Unlimited diagrams',
      'Export to SVG',
      'Priority support',
    ],
  },
  'export-svg': {
    title: 'SVG Export is a Pro Feature',
    description: 'Upgrade to Pro or Lifetime to export your diagrams as SVG files.',
    icon: Sparkles,
    benefits: [
      'Export to SVG format',
      'Unlimited diagrams',
      'Priority support',
    ],
  },
  general: {
    title: 'Upgrade to Pro',
    description: 'Unlock all features with a Pro or Lifetime subscription.',
    icon: Crown,
    benefits: [
      'Unlimited diagrams',
      'Export to all formats',
      'Priority support',
    ],
  },
};

export function UpgradePrompt({ 
  isOpen, 
  onClose, 
  feature,
  currentCount,
  maxCount,
}: UpgradePromptProps) {
  const navigate = useNavigate();

  const config = featureMessages[feature];
  const Icon = config.icon;

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm mb-4">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-2xl font-bold text-white mb-2">
            {config.title}
          </DialogTitle>
          <DialogDescription className="text-white/80 text-sm">
            {config.description}
          </DialogDescription>
          {feature === 'diagrams' && currentCount !== undefined && maxCount !== undefined && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm">
              <span className="font-semibold">{currentCount}/{maxCount}</span>
              <span className="text-white/70">diagrams used</span>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="px-6 py-6">
          <p className="text-sm font-medium mb-4 text-muted-foreground">
            With Pro or Lifetime you get:
          </p>
          <ul className="space-y-3 mb-6">
            {config.benefits.map((benefit, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.15, delay: index * 0.05 }}
                className="flex items-center gap-3"
              >
                <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                  <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-foreground">{benefit}</span>
              </motion.li>
            ))}
          </ul>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Maybe Later
            </Button>
            <Button onClick={handleUpgrade} className="flex-1 gap-2">
              <Crown className="w-4 h-4" />
              Upgrade Now
            </Button>
          </div>

          {/* Pricing Note */}
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Starting at $4.99/month or $49.99 lifetime
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradePrompt;
